import {ColumnDef} from '@tanstack/react-table';
import {ReadAudience} from '@tinct/core-api-models';
import {Button, LongText, Tooltip, TooltipContent, TooltipTrigger,} from '@tinct/web-components';
import {CollectionColumnHeader, CollectionRowActions} from '@tinct/web-collection';
import {Link} from 'react-router-dom';
import {IconInfoCircle, IconPencil, IconTrash,} from '@tabler/icons-react';
import {useCurrentWorkspaceUrlPrefix} from "@tinct/core-workspace";

type UseAudiencesListColumnsOptions = {
  onDeleteAudience?: (audience: ReadAudience) => void;
};

export function useAudiencesListColumns(
  options: UseAudiencesListColumnsOptions = {},
): ColumnDef<ReadAudience>[] {
  const { onDeleteAudience } = options;
  const workspaceBaseUrl = useCurrentWorkspaceUrlPrefix();
  return [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Audience" />
      ),
      cell: ({ row }) => {
        const name = row.original.name ?? '-';
        const importStatus = row.original.lastImportJob?.status;

        if(importStatus === 'running') {
          return (
            <div className="text-muted-foreground font-semibold truncate max-w-[500px]">{name} - Enrichment in progress...</div>
          );
        }

        return (
          <Button variant="link" className="p-0 h-auto" asChild>
            <Link to={`${workspaceBaseUrl}/audiences/${row.original.id}`}>
              <LongText className="max-w-[500px] font-semibold">{name}</LongText>
            </Link>
          </Button>
        );
      },
      size: 677,
      minSize: 200,
      meta: { className: 'w-full' },
      enableHiding: false
    },
    {
      accessorKey: 'statistics.leadsCount',
      header: ({ column }) => (
        <div className="flex items-center gap-1">
          <CollectionColumnHeader column={column} title="Companies" />
          <Tooltip>
            <TooltipTrigger asChild>
              <IconInfoCircle className="size-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>Number of companies in this audience</TooltipContent>
          </Tooltip>
        </div>
      ),
      cell: ({ row }) => {
        const count = row.original.statistics?.leadsCount ?? 0;
        const importStatus = row.original.lastImportJob?.status;

        if(importStatus === 'running') {
          return "-";
        }
        return <div>{count}</div>;
      },
      size: 98,
      enableSorting: false,
    },
    {
      accessorKey: 'statistics.campaignsCount',
      header: ({ column }) => (
        <div className="flex items-center gap-1">
          <CollectionColumnHeader column={column} title="Campaigns" />
          <Tooltip>
            <TooltipTrigger asChild>
              <IconInfoCircle className="size-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>Number of campaigns using this audience</TooltipContent>
          </Tooltip>
        </div>
      ),
      cell: ({ row }) => {
        const count = row.original.statistics?.campaignsCount ?? 0;
        const importStatus = row.original.lastImportJob?.status;

        if(importStatus === 'running') {
          return "-";
        }
        return <div>{count}</div>;
      },
      size: 98,
      enableSorting: false,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Created" />
      ),
      cell: ({ row }) => {
        const createdAt = row.original.createdAt;
        if (!createdAt) return <div className="text-muted-foreground">-</div>;

        const date = new Date(createdAt);
        return (
          <div className="text-muted-foreground">
            {date.toLocaleDateString('en-GB')}
          </div>
        );
      },
      size: 109,
      enableHiding: false
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const importStatus = row.original.lastImportJob?.status;

        if(importStatus === 'running') {
          return null;
        }
        return <div className="flex justify-end">
          <CollectionRowActions
            row={row}
            actions={[
              {
                label: 'Edit',
                icon: IconPencil,
                linkTo: (audience: ReadAudience) => `${workspaceBaseUrl}/audiences/${audience.id}`,
              },
              {
                label: 'Delete',
                icon: IconTrash,
                group: 'danger',
                className: 'text-red-500!',
                onClick: (audience: ReadAudience) => {
                  onDeleteAudience?.(audience);
                },
              },
            ]}
          />
        </div>
      },
      size: 74,
    },
  ];
}
